import type { createPortal as ReactDOMCreatePortal } from 'react-dom'
import type { RemoteComponent, RemoteComponentType, RemoteRoot, RemoteText } from '@remote-ui/core'
import type { HostConfig } from 'react-reconciler'

export type WixRemoteRoot = RemoteRoot & { key: string }

type Type = RemoteComponentType<string, any>
type Props = Record<string, unknown>
type ViewInstance = RemoteComponent<any, any>
type TextInstance = RemoteText<any>
type HostContext = Record<string, never>
type UpdatePayload = Record<string, unknown>
type SuspenseInstance = never
type PublicInstance = unknown
type HydratableInstance = unknown
type ChildSet = unknown
type TimeoutHandle = unknown
type NoTimeout = unknown

export type ReconcilerConfig = HostConfig<
	Type,
	Props,
	WixRemoteRoot,
	ViewInstance,
	TextInstance,
	SuspenseInstance,
	HydratableInstance,
	PublicInstance,
	HostContext,
	UpdatePayload,
	ChildSet,
	TimeoutHandle,
	NoTimeout
>

export type ConfigMapper = (config: ReconcilerConfig) => ReconcilerConfig

export interface ExternalComponentRendererAPI {
	registerRemoteUiPort: ({ port }: { port: MessagePort }) => void
}

export enum SerializedType {
	WINDOW = 'window',
	EVENT = 'event',
	NATIVE_EVENT = 'nativeEvent',
	ELEMENT = 'element',
	DOCUMENT = 'document',
}

export interface SerializedValue extends Record<string, any> {
	$type$: SerializedType
}

export interface SerializedElement extends SerializedValue {
	$type$: SerializedType.ELEMENT
	remoteId: string
	value?: any
}

export interface SerializedEvent extends SerializedValue {
	$type$: SerializedType.EVENT
}

export interface RemoteEventHandlerResponse {
	returnValue: any
	defaultPrevented: boolean
}

export type FetchFn = (url: string, init?: RequestInit) => Promise<Response>
export type FetchOptions = {
	timeout?: number
	maxRetries?: number
	fetchFn?: FetchFn
} & Record<string, any>
export type RemoteRenderFunc = (root: RemoteRoot, compGuid: string, props?: any) => Promise<void>
export type SSRRemoteRenderFunc = (root: RemoteRoot, props?: any) => Promise<void>

export interface RemoteReactDom {
	createPortal: typeof ReactDOMCreatePortal
}

export type RemotePortalCreator = (container: Element, id?: string) => Promise<{ id: string }>

export type { RenderByocSSRApi } from './ssrSandbox/ssrEndpoint'
